import { usePageMeta } from '../usePageMeta';

const LAST_UPDATED = 'May 4, 2026';
const CONTACT_EMAIL = 'contact@inafana.com';

export function Privacy(): React.JSX.Element {
  usePageMeta('Privacy Policy — Inafana');

  return (
    <section className="legal-section">
      <div className="container">
        <article className="legal">
          <div className="legal-meta">Last updated: {LAST_UPDATED}</div>
          <h1>Privacy Policy</h1>

          <p>
            This policy explains what information Inafana ("we", "us") collects when you
            visit this website, how we use it, and the choices you have. We try to keep this
            short and in plain English.
          </p>

          <h2>Who we are</h2>
          <p>
            Inafana is a software studio that builds custom AI agents and internal tools for
            small and mid-sized businesses. The data controller for this website is Inafana.
            You can reach us at{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>

          <h2>What we collect</h2>
          <ul>
            <li>
              <strong>Contact form submissions.</strong> When you fill out the contact form,
              we store the name, email address, subject, and message you provide so we can
              reply.
            </li>
            <li>
              <strong>Newsletter sign-ups.</strong> If you submit your email to our newsletter,
              we store that email address.
            </li>
            <li>
              <strong>Basic page-view analytics (first-party).</strong> When you load a page
              on this site, our own server records the path you visited, the referring URL,
              and your browser's User-Agent string, with a timestamp. We do not record your
              IP address in this first-party log.
            </li>
            <li>
              <strong>Google Analytics (third-party, consent-based).</strong> We use Google
              Analytics 4 (measurement ID <code>G-3Y0196KDB1</code>) to understand aggregate
              traffic patterns. On your first visit a banner asks whether to enable analytics
              cookies:
              <ul>
                <li>
                  <strong>If you accept</strong>, Google Analytics sets cookies in your browser
                  (typically <code>_ga</code> and a per-property <code>_ga_*</code>) and sends
                  information about your visit — page paths, referrer, device/browser type,
                  and IP address — to Google. Google may anonymise the IP and processes the
                  data per its own privacy policies.
                </li>
                <li>
                  <strong>If you decline (or have not yet chosen)</strong>, no analytics
                  cookies are set. Anonymised, cookieless traffic pings may still be sent to
                  Google so we can see aggregate volume, but they cannot be used to identify
                  or follow you across visits.
                </li>
              </ul>
              Admin pages (<code>/admin/*</code>) are never tracked by Google Analytics. You
              can change your decision at any time by clearing this site's data in your
              browser settings — the banner will reappear on your next visit.
            </li>
            <li>
              <strong>Meta Pixel (third-party, consent-based).</strong> We advertise on
              Facebook and Instagram, and use the Meta Pixel (ID{' '}
              <code>1291570188568931</code>) to measure which ads lead to enquiries. It is
              covered by the same consent banner as Google Analytics — nothing is sent to
              Meta unless you accept.
              <ul>
                <li>
                  <strong>If you accept</strong>, the pixel sets cookies (typically{' '}
                  <code>_fbp</code>, and <code>_fbc</code> if you arrived from a Meta ad)
                  and sends Meta the pages you view on this site, plus a{' '}
                  <code>Lead</code> event when you successfully submit the project brief
                  form. That Lead event is reported twice — once from your browser and once
                  from our server (Meta's Conversions API) — with a shared ID so Meta counts
                  it as one enquiry. The server-side copy additionally includes your IP
                  address and browser User-Agent, and your name, email address and phone
                  number in <strong>hashed</strong> form: they are put through a one-way
                  SHA-256 function before they leave our server, so Meta can check them
                  against hashes it already holds but cannot read them back out of the
                  hash. We never send Meta the text of your brief or your company name.
                </li>
                <li>
                  <strong>If you decline (or have not yet chosen)</strong>, the pixel stays
                  switched off: no cookies are set and no events, page views included, are
                  sent to Meta. The server-side copy is switched off with it — your
                  submission is stored and emailed to us as normal, and nothing about it
                  reaches Meta.
                </li>
              </ul>
              Admin pages (<code>/admin/*</code>) are never tracked by the Meta Pixel.
            </li>
            <li>
              <strong>Admin session cookies.</strong> If you sign in to the internal admin
              portal, we set an HTTP-only session cookie. This cookie is not used on the
              public website and not set for ordinary visitors.
            </li>
          </ul>

          <h2>How we use it</h2>
          <ul>
            <li>To respond to your enquiries and provide the services you request.</li>
            <li>To send you the newsletter you subscribed to (you can unsubscribe at any time).</li>
            <li>To understand which pages are read and improve the site.</li>
            <li>To detect and prevent abuse (e.g. spam submissions, brute-force login attempts).</li>
          </ul>

          <h2>Sharing</h2>
          <p>
            We do not sell your personal information, and we never pass your contact details
            (name, email, phone, or the content of your enquiry) to advertisers. We share data
            only with the infrastructure, analytics, and ad-measurement providers needed to
            operate and promote the site:
          </p>
          <ul>
            <li>Our server and database hosting provider (for storing leads, messages, and similar).</li>
            <li>
              Google LLC, for Google Analytics traffic analysis. See{' '}
              <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">
                Google's Privacy Policy
              </a>{' '}
              for how Google handles the data it receives.
            </li>
            <li>
              Meta Platforms, Inc., for measuring our Facebook and Instagram advertising —
              and only if you accept the consent banner. This includes the hashed contact
              details described above, sent from our server so the enquiry can be matched
              to the ad that produced it. See{' '}
              <a
                href="https://www.facebook.com/privacy/policy/"
                target="_blank"
                rel="noopener noreferrer"
              >
                Meta's Privacy Policy
              </a>{' '}
              for how Meta handles the data it receives.
            </li>
          </ul>

          <h2>How long we keep it</h2>
          <p>
            Contact form submissions and newsletter subscriptions are kept until you ask us to
            delete them. Page-view records are kept for up to 24 months for analytics purposes.
            Admin session cookies expire after 7 days of inactivity.
          </p>

          <h2>Cookies</h2>
          <p>
            Our own server <strong>does not set any cookies</strong> for ordinary visitors of
            the public site. The only cookies the public site can set are Google Analytics's{' '}
            <code>_ga</code> and <code>_ga_*</code> cookies and the Meta Pixel's{' '}
            <code>_fbp</code> and <code>_fbc</code> cookies, and only after you accept them
            via the consent banner. If you decline, none of them are written.
          </p>
          <p>
            A small entry under your browser's local storage
            (<code>inafana.cookie-consent</code>) records your choice so we don't show the
            banner on every visit. Local storage isn't a cookie and isn't sent to any server;
            it lives only in your browser.
          </p>
          <p>
            The admin portal sets a single HTTP-only, Secure, SameSite=Lax session cookie
            when an admin signs in; that cookie is the only thing storing the session on
            your device, and it is never set for ordinary visitors.
          </p>
          <p>
            You can also opt out of Google Analytics globally with the{' '}
            <a
              href="https://tools.google.com/dlpage/gaoptout"
              target="_blank"
              rel="noopener noreferrer"
            >
              Google Analytics Opt-out Browser Add-on
            </a>{' '}
            or by blocking third-party cookies in your browser settings. Meta ad-tracking
            preferences can be managed in your{' '}
            <a
              href="https://www.facebook.com/settings?tab=ads"
              target="_blank"
              rel="noopener noreferrer"
            >
              Facebook ad settings
            </a>
            .
          </p>

          <h2>Your rights</h2>
          <p>
            You can ask us to access, correct, or delete the personal data we hold about you,
            or to stop processing it. Email{' '}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> and we will respond within
            30 days. If you are in the EU/UK, you also have the right to lodge a complaint
            with your local data protection authority.
          </p>

          <h2>Security</h2>
          <p>
            Data is encrypted in transit (HTTPS) and at rest. Admin access requires a password
            and is rate-limited. We follow standard practices to protect against unauthorised
            access, but no system is perfectly secure — if you believe your data has been
            compromised, please contact us immediately.
          </p>

          <h2>Changes to this policy</h2>
          <p>
            We may update this policy as the site evolves. The "Last updated" date at the top
            will reflect the most recent change. Material changes will be announced on this
            page.
          </p>

          <h2>Contact</h2>
          <p>
            Questions? Email <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
          </p>
        </article>
      </div>
    </section>
  );
}
